import React, { createContext, useContext, useState, useEffect } from 'react';
import { TokenStorage, AuthAPI, apiFetch } from '../services/api';

interface User {
  id: string;
  email: string;
  full_name: string;
  phone: string;
 role: {
    role_id: number;
    name: string;
    status: string;
    account_type: string;
  };
  organisation: string;
  is_verified: boolean;
  date_joined: string;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<any>; // or a proper LoginResponse type
  // Resolves to `true` when this Google sign-in just created a brand-new
  // account, so the caller can send first-timers to /choose-plan instead
  // of straight to /dashboard (same as the email/password + OTP flow).
  loginWithGoogle: (idToken: string) => Promise<boolean>;
  logout: () => Promise<void>;
  setUserFromTokens: (user: User, access: string, refresh: string) => void;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);
export function getRoleName(role?: User['role']): string {
  if (!role) return '';
  return typeof role === 'string' ? role : role.name;
}

// Roles assigned to someone who signed themselves up (and therefore owns
// the account/subscription). Anyone whose role is NOT one of these signed
// in via a team invite instead — see TeamManagement.tsx, where invites are
// only ever sent with role 'admin' or 'member'.
const ACCOUNT_OWNER_ROLES = ['researcher', 'institution', 'publisher'];

/**
 * True for a user who joined an existing account as an invited team member
 * (role 'admin' or 'member'), rather than the person who created the
 * account and owns its subscription. Invited members shouldn't see or
 * access the Subscription, Team, Billing, or API sections.
 */
export function isInvitedMember(role?: User['role']): boolean {
  const name = getRoleName(role).toLowerCase();
  return name !== '' && !ACCOUNT_OWNER_ROLES.includes(name);
}

function normalizeUser(data: any): User {
  return {
    ...data,
    role: typeof data.role === 'string'
      ? { role_id: 0, name: data.role, status: 'Active', account_type: data.role }
      : data.role,
  };
}
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // On mount — verify token is still valid by calling /auth/me/
  // If expired, apiFetch will auto-refresh or redirect to /login
  useEffect(() => {
    async function restoreSession() {
      const token = TokenStorage.getAccess();
      const refresh = TokenStorage.getRefresh();

      if (!token && !refresh) {
        setLoading(false);
        return;
      }

      try {
        const data = await apiFetch('/auth/me/');
        if (data) {
          TokenStorage.setUser(data);
          setUser(data);
        } else {
          // apiFetch returned null — refresh failed, already redirected to /login
          TokenStorage.clear();
        }
      } catch {
        // me/ failed even after refresh attempt — clear and let Protected route handle it
        TokenStorage.clear();
        setUser(null);
      } finally {
        setLoading(false);
      }
    }
    restoreSession();
  }, []);

  const setUserFromTokens = (user: User, access: string, refresh: string) => {
    const normalized = normalizeUser(user);
    TokenStorage.setTokens(access, refresh);
    TokenStorage.setUser(normalized);
    setUser(normalized);
  };

  const login = async (email: string, password: string) => {
    const data = await AuthAPI.login({ email, password });
    setUserFromTokens(data.user, data.tokens.access, data.tokens.refresh);
    await refreshUser()
    return data;
  };

  // Google Sign-In hits the SAME /auth/login/ endpoint as the normal login,
  // just with google_login=1. We decode the ID token client-side only to
  // grab `email`/`full_name` for convenience — the raw `id_token` is also
  // sent so the backend can verify it with Google itself.
  const loginWithGoogle = async (idToken: string): Promise<boolean> => {
    if (!idToken) {
      throw new Error('Google sign-in failed. Please try again.');
    }

    const payload = JSON.parse(atob(idToken.split('.')[1])) as {
      email?: string;
      name?: string;
    };

    if (!payload.email) {
      throw new Error('Google account has no email. Please try again.');
    }

    const data = await AuthAPI.login({
      email: payload.email,
      google_login: 1,
      id_token: idToken,          // ✅ raw JWT — backend verifies this with Google
      full_name: payload.name || '',
    });
    setUserFromTokens(data.user, data.tokens.access, data.tokens.refresh);

    // Explicitly hit /auth/me/ right after a Google login so the session
    // is verified against the server and the user object in state/storage
    // reflects the freshest data (role, verification status, etc.).
    try {
      const meData = await apiFetch('/auth/me/');
      if (meData) {
        const normalized = normalizeUser(meData);
        TokenStorage.setUser(normalized);
        setUser(normalized);
      }
    } catch {
      // Non-fatal — user is already logged in from the tokens above.
    }

    // Prefer an explicit flag from the backend if it sends one.
    if (typeof data.is_new_user === 'boolean') return data.is_new_user;
    if (typeof data.created === 'boolean') return data.created;

    // Fallback heuristic: a brand-new Google account is created and logged
    // in within the same request, so `date_joined` will be effectively
    // "now". Anything older means this is a returning user.
    const joined = data.user?.date_joined ? new Date(data.user.date_joined).getTime() : 0;
    return joined > 0 && Date.now() - joined < 2 * 60 * 1000;
  };

  const logout = async () => {
    try {
      const refresh = TokenStorage.getRefresh();
      if (refresh) await AuthAPI.logout(refresh);
    } catch (_) { }
    TokenStorage.clear();
    setUser(null);
  };

  const refreshUser = async () => {
    try {
      const data = await apiFetch('/auth/me/');
      if (data) {
        const normalized = normalizeUser(data);
        TokenStorage.setUser(normalized);
        setUser(normalized)
      }
    } catch { }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, loginWithGoogle, logout, setUserFromTokens, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

